import { PropsWithChildren, useRef, useState } from 'react';
import { Button, Col, Container, Modal, Row } from 'react-bootstrap';

import { useAppDispatch, useAppSelector } from '@/ui/store/helperRedux';
import { changeLoginState, changeRecoveryCodeState, changeRecoveryState } from '@/ui/store/slices/showModalsSlice';

import { useScreenSize } from '@/ui/hooks/useScreenSize';
import type { ScreenSize } from '@/ui/hooks/useScreenSize';

import UserController from '@/controllers/UserController';

import { VALIDATE_EMAIL } from '@/data/libraries/ConfigParameters';

import validatePattern from '@/domain/utils/validatePattern';
import { translate } from '@/domain/utils/translate';
import { getUrlFromCssVariable } from '@/domain/utils/CssToImage';

import Icon from '@/ui/assets/Icon';
import BackIcon from '@/ui/assets/BackIcon';
import HeaderMobileUser from '@/ui/components/user/HeaderMobileUser';
import InputForm from '@/ui/components/forms/InputForm';

interface PropsSingUpForm {
  setEmail: (email: string) => void;
}

/** Componente formulario para recuperar contraseña
 *@component
 */
const PasswordRecovery = ({
  setEmail,
}: PropsWithChildren<PropsSingUpForm>): JSX.Element => {

  /** URL del logo de modales */
  const LOGO = getUrlFromCssVariable('--img-logo-modals-white');
  /** Actualización de estado Redux */
  const dispatch = useAppDispatch();

  /** Evento que muestra y oculta el modal para login*/
  const handleShowLogin = (): void => {
    dispatch(changeLoginState);
  }

  /** Estado inicial del modal para Registro */
  const showRecoveryModal: boolean = useAppSelector((state) => state.showModals.showRecovery);
  /** Evento que muestra y oculta el modal para registro*/
  const handleShowPasswordRecovery = (): void => {
    dispatch(changeRecoveryState());
  }

  /** Evento que muestra y oculta el modal para registro*/
  const handleShowPasswordRecoveryCode = (): void => {
    dispatch(changeRecoveryCodeState());
  }

  /** Controlador de eventos para usuario */
  const { getAuthCode } = UserController();

  /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido  */
  const { width, maxLargeWidth }: ScreenSize = useScreenSize();

  const emailRef = useRef<HTMLInputElement>(null);

  /** Habilita un botón para avanzar a la siguiente fase */
  const enableButton = (): void => setStateButton('btn-primary-text-standard');

  const disableButton = (): void => setStateButton('btn-primary-text-standard disabled');

  /** Declaración inicial de los botones como deshabilitado */
  const [stateButton, setStateButton] = useState<string>('btn-primary-text-standard disabled');

  /** Declaración inicial un input en su estado normal */
  const [inputWithError, setInputWithError] = useState<boolean>(false);

  /** Maneja el state messageError para los inputs */
  const [messageError, setMessageError] = useState<string>('');

  const [buttonText, setButtonText] = useState<string>(translate('session.continue_text'));

  const setValidInput = (): void => {
    setMessageError('');
    setInputWithError(false);
    enableButton();
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = validatePattern(VALIDATE_EMAIL, email);
    return emailRegex;
  };

  const handleEmailValidation = (): void => {
    const emailValue = emailRef.current?.value.toLocaleLowerCase() ?? '';
    if (emailValue === '') {
      setInputWithError(false);
      setMessageError('');
    } else if (!validateEmail(emailValue)) {
      setInputWithError(true);
      setMessageError(INVALID_EMAIL_TEXT);
      disableButton();
    } else {
      setValidInput();
    }
  };

  const handleInput = async (): Promise<boolean> => {
    handleEmailValidation();
    return !inputWithError;
  };

  const toLogin = (): void => {
    handleShowPasswordRecovery();
    handleShowLogin();
  };

  const toPasswordRecoveryCode = (): void => {
    handleShowPasswordRecovery();
    handleShowPasswordRecoveryCode();
  };

  /** Solicita código de autorización para cambio de contraseña */
  const handleSubmit = async (): Promise<void> => {
    // Deshabilitar el botón
    disableButton();
    setButtonText(CHARGING_TEXT);
    const emailValue = emailRef.current?.value.toLocaleLowerCase() ?? '';
    try {
      const result = await getAuthCode(emailValue);
      if (result) {
        setEmail(emailValue);
        toPasswordRecoveryCode();
      } else {
        setInputWithError(true);
        setMessageError(EMAIL_NO_EXIST);
        setButtonText(CONTINUE_TEXT);
      }
    } catch (error) {
      console.error(error);
    }

    if (!Error) {
      enableButton();
      setButtonText(CONTINUE_TEXT);
    }
  };

  /** Constantes de texto */
  const RECOVERY_TITLE: string = translate('session.recovery_password_title');
  const EMAIL_LABEL: string = translate('session.email');
  const CONTINUE_TEXT: string = translate('session.continue_text');
  const CHARGING_TEXT: string = translate('session.charging_text');
  const INVALID_EMAIL_TEXT: string = translate('session.invalid_email_text');
  const EMAIL_NO_EXIST: string = translate('session.email_no_exist');
  const EMAIL_TEXT: string = translate('session.email_help_text');

  /** Variable que define si es entorno mobile o desktop */
  const isMobile: boolean = width < maxLargeWidth;
  return (
    <Modal show={showRecoveryModal} fullscreen={true} animation={false}>
      {isMobile ? (
        <Modal.Header className=' d-flex p-0  bg-ilis-modal '>
          <HeaderMobileUser
            handleIconClick={toLogin}
            logoSrc={LOGO}
            iconClass='btn-icon-dark svg-primary-4'
            logoClass='logo-header-modal'
          />
        </Modal.Header>
      ) : (
        ''
      )}
      <Modal.Body className='p-0'>
        <Container fluid>
          <Row
            className={isMobile ? 'd-flex align-items-end flex-column p-0 ' : 'min-vh-100 d-flex align-items-end flex-column p-0  '}
          >
            {!isMobile && (
              <Col xs={12} className='bg-ilis-modal min-vh-50'>
                <Col xs={12} className='align-self-center d-flex justify-content-around pb-5 '>
                  <Col lg={11} xs={12} className='text-start'>
                    <BackIcon
                      onClick={toLogin}
                      classMain='btn-icon-dark my-5 svg-primary-4 no-pb-mt'
                    />
                  </Col>
                  <div>
                    <img
                      src={LOGO}
                      alt='logo'
                      className='login-desktop-logo d-none d-sm-block position-absolute top-10 start-10 logo-full-modal'
                    />
                  </div>
                </Col>
              </Col>
            )}

            <Col
              xs={12}
              sm={6}
              lg={4}
              className={
                isMobile
                  ? 'bg-primary-4 rounded-top-5  '
                  : `bg-primary-4 position-absolute top-50 start-50 translate-middle rounded-top-5 mt-5 ${!isMobile && 'shadow'}`
              }>
              <Row>
                <Col xs={12} className='min-vh-75 d-flex align-items-center justify-content-center my-auto flex-column  '>
                  <Col xs={12} className='pb-5 text-center d-flex justify-content-center align-items-center'>
                    <span>
                      <Icon name='ico-key'></Icon>
                    </span>
                    <span className='h2 fw-bold text-secondary-1 px-3'>{RECOVERY_TITLE}</span>
                  </Col>
                  <Col xs={10} lg={7}>
                    <span className='text-secondary-2 paragraph'>{EMAIL_TEXT}</span>
                    <div>
                      <InputForm
                        className='mb-3 mt-4 '
                        label={EMAIL_LABEL}
                        classLabel='paragraph text-secondary-2 '
                        inputRef={emailRef}
                        type='email'
                        iconName='ico-error'
                        onChange={handleInput}
                        withErrors={inputWithError}
                        lastIconName={inputWithError ? 'ico-info' : ''}
                      />
                    </div>
                    <Col className='my-4 text-danger p-regular'>
                      <span>{messageError}</span>
                    </Col>
                    <div className='d-grid gap-2 mt-4 no-pb-mt'>
                      <Button className={stateButton} type='button' onClick={handleSubmit}>
                        <span className='p-large '>{buttonText}</span>
                      </Button>
                    </div>
                  </Col>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
    </Modal>
  );
};

export default PasswordRecovery;
